import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Megaphone,
  Calendar,
  ChevronRight,
  Pin,
  AlertCircle,
  Info,
  Bell,
  Clock,
  X,
  CalendarOff,
  Monitor,
  MapPin,
  Laptop,
  Users,
  Repeat,
  Star,
  Globe,
  GraduationCap,
} from 'lucide-react';
import { apiCall, api } from '../utils/api';
import type { Holiday, ClassModeSetting } from '../types';

export interface Announcement {
  id: string;
  title: string;
  content: string;
  category: 'general' | 'academic' | 'deadline' | 'event' | 'urgent';
  audience: 'public' | 'faculty' | 'students';
  isPinned: boolean;
  datePosted: string;
  expiresAt?: string;
  postedBy: string;
}

interface BulletinBoardProps {
  maxItems?: number;
  compact?: boolean;
  userRole?: 'administrator' | 'student' | 'faculty' | null;
}

type ActiveSection = 'announcements' | 'holidays' | 'classmode';

const categoryConfig: Record<
  string,
  { icon: typeof Megaphone; color: string; bgColor: string; badgeClass: string; label: string }
> = {
  general: {
    icon: Info,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 border-blue-200',
    badgeClass: 'bg-blue-100 text-blue-700 border-blue-200',
    label: 'General',
  },
  academic: {
    icon: Megaphone,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50 border-indigo-200',
    badgeClass: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    label: 'Academic',
  },
  deadline: {
    icon: Clock,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50 border-amber-200',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200',
    label: 'Deadline',
  },
  event: {
    icon: Calendar,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 border-emerald-200',
    badgeClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    label: 'Event',
  },
  urgent: {
    icon: AlertCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50 border-red-200',
    badgeClass: 'bg-red-100 text-red-700 border-red-200',
    label: 'Urgent',
  },
};

const audienceConfig: Record<string, { icon: typeof Globe; label: string; badgeClass: string }> = {
  public: {
    icon: Globe,
    label: 'Public',
    badgeClass: 'bg-sky-50 text-sky-700 border-sky-200',
  },
  faculty: {
    icon: Users,
    label: 'All Faculty',
    badgeClass: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  students: {
    icon: GraduationCap,
    label: 'All Students',
    badgeClass: 'bg-teal-50 text-teal-700 border-teal-200',
  },
};

const holidayTypeConfig: Record<string, { color: string; bgColor: string; label: string }> = {
  regular: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50 border-emerald-200',
    label: 'Regular Holiday',
  },
  special: {
    color: 'text-purple-700',
    bgColor: 'bg-purple-50 border-purple-200',
    label: 'Special Holiday',
  },
  academic: {
    color: 'text-blue-700',
    bgColor: 'bg-blue-50 border-blue-200',
    label: 'Academic Holiday',
  },
};

const classModeConfig: Record<string, { icon: typeof Monitor; color: string; bgColor: string; gradient: string; label: string }> = {
  'face-to-face': {
    icon: MapPin,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 border-emerald-200',
    gradient: 'from-emerald-500 to-emerald-600',
    label: 'Face-to-Face',
  },
  online: {
    icon: Laptop,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 border-blue-200',
    gradient: 'from-blue-500 to-blue-600',
    label: 'Online',
  },
  hybrid: {
    icon: Users,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50 border-violet-200',
    gradient: 'from-violet-500 to-violet-600',
    label: 'Hybrid',
  },
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatHolidayDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
}

function isUpcoming(dateStr: string): boolean {
  const date = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return date >= now;
}

function getDaysUntil(dateStr: string): number {
  const date = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export function BulletinBoard({ maxItems, compact = false, userRole }: BulletinBoardProps) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [classModeSettings, setClassModeSettings] = useState<ClassModeSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [activeSection, setActiveSection] = useState<ActiveSection>('announcements');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [announcementData, holidayData, classModeData] = await Promise.all([
        apiCall<{ announcements: Announcement[] }>('/announcements').catch(() => ({ announcements: [] })),
        api.getPublicHolidays().catch(() => ({ holidays: [] })),
        api.getPublicClassModeSettings().catch(() => ({ settings: [] })),
      ]);
      setAnnouncements(announcementData.announcements || []);
      setHolidays(holidayData.holidays || []);
      setClassModeSettings(classModeData.settings || []);
    } catch {
      setAnnouncements([]);
      setHolidays([]);
      setClassModeSettings([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAnnouncements = announcements
    .filter((a) => {
      const aud = a.audience || 'public';
      if (aud === 'public') return true;
      if (aud === 'faculty' && (userRole === 'faculty' || userRole === 'administrator')) return true;
      if (aud === 'students' && (userRole === 'student' || userRole === 'administrator')) return true;
      if (userRole === 'administrator') return true;
      return false;
    })
    .filter((a) => activeFilter === 'all' || a.category === activeFilter)
    .sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.datePosted).getTime() - new Date(a.datePosted).getTime();
    })
    .slice(0, maxItems || 999);

  // Sort holidays: upcoming first, then by date
  const sortedHolidays = [...holidays].sort((a, b) => {
    const aUp = isUpcoming(a.date);
    const bUp = isUpcoming(b.date);
    if (aUp && !bUp) return -1;
    if (!aUp && bUp) return 1;
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  // Current/active class mode setting
  const now = new Date();
  const activeClassMode = classModeSettings.find((s) => {
    const from = new Date(s.effectiveFrom);
    const until = s.effectiveUntil ? new Date(s.effectiveUntil) : null;
    return from <= now && (!until || until >= now);
  });

  const filterTabs = [
    { key: 'all', label: 'All' },
    { key: 'urgent', label: 'Urgent' },
    { key: 'academic', label: 'Academic' },
    { key: 'deadline', label: 'Deadlines' },
    { key: 'event', label: 'Events' },
    { key: 'general', label: 'General' },
  ];

  const sectionTabs = [
    {
      key: 'announcements' as ActiveSection,
      label: 'Announcements',
      icon: Megaphone,
      count: announcements.length,
      color: 'text-amber-600',
      activeColor: 'bg-amber-600',
      activeBg: 'bg-amber-50',
    },
    {
      key: 'holidays' as ActiveSection,
      label: 'Holidays',
      icon: CalendarOff,
      count: holidays.filter((h) => isUpcoming(h.date)).length,
      color: 'text-emerald-600',
      activeColor: 'bg-emerald-600',
      activeBg: 'bg-emerald-50',
    },
    {
      key: 'classmode' as ActiveSection,
      label: 'Class Mode',
      icon: Monitor,
      count: classModeSettings.length,
      color: 'text-blue-600',
      activeColor: 'bg-blue-600',
      activeBg: 'bg-blue-50',
    },
  ];

  if (loading) {
    return (
      <div className="space-y-4">
        {/* Skeleton tabs */}
        <div className="flex gap-2 mb-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse h-11 w-36 bg-gray-200 rounded-xl" />
          ))}
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-24 bg-gray-200 rounded-xl" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* ═══ Section Tabs ═══ */}
      {!compact && (
        <div className="flex flex-col gap-4 mb-6">
          {/* Main section tabs */}
          <div className="flex items-stretch gap-1.5 sm:gap-2 md:gap-3 overflow-x-auto pb-1 scrollbar-hide">
            {sectionTabs.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeSection === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => { setActiveSection(tab.key); setActiveFilter('all'); }}
                  className={`flex items-center gap-1.5 sm:gap-2 md:gap-2.5 px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-300 flex-1 sm:flex-initial justify-center sm:justify-start min-w-0 ${
                    isActive
                      ? `${tab.activeColor} text-white shadow-lg`
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <TabIcon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-white' : tab.color}`} />
                  <span className="hidden sm:inline truncate">{tab.label}</span>
                  <span className="sm:hidden text-[11px] truncate">{tab.label}</span>
                  {tab.count > 0 && (
                    <span className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full font-bold ${
                      isActive ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Category filter pills (announcements only) */}
          {activeSection === 'announcements' && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {filterTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeFilter === tab.key
                      ? 'bg-amber-100 text-amber-800 border border-amber-300 shadow-sm'
                      : 'bg-white text-gray-500 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {tab.label}
                  {tab.key !== 'all' && (
                    <span className="ml-1 text-[10px] opacity-70">
                      ({announcements.filter((a) => a.category === tab.key).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ Announcements Section ═══ */}
      {activeSection === 'announcements' && (
        <ScrollArea className={compact ? 'h-[400px]' : 'h-auto'}>
          <div className="space-y-3">
            {filteredAnnouncements.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-gray-500 font-medium">No announcements found</p>
                <p className="text-gray-400 text-sm mt-1">Check back later for updates</p>
              </div>
            ) : (
              filteredAnnouncements.map((announcement) => {
                const config = categoryConfig[announcement.category] || categoryConfig.general;
                const CategoryIcon = config.icon;

                return (
                  <Card
                    key={announcement.id}
                    className={`group cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-[0.99] hover:-translate-y-0.5 border ${
                      announcement.isPinned
                        ? 'border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50/40 to-white shadow-sm ring-1 ring-amber-100/50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedAnnouncement(announcement)}
                  >
                    <CardContent className={`${compact ? 'p-3' : 'p-3.5 sm:p-4 md:p-5'}`}>
                      <div className="flex items-start gap-2.5 sm:gap-3 md:gap-4">
                        <div
                          className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center ${config.bgColor} border transition-transform group-hover:scale-110`}
                        >
                          <CategoryIcon className={`w-5 h-5 ${config.color}`} />
                        </div>

                          <div className="flex-1 min-w-0 max-w-full overflow-hidden">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {announcement.isPinned && (
                              <Pin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            )}
                            <h3
                                className={`min-w-0 max-w-full font-semibold text-gray-900 [overflow-wrap:anywhere] break-words line-clamp-3 ${
                                compact ? 'text-sm' : 'text-sm md:text-base'
                              }`}
                            >
                              {announcement.title}
                            </h3>
                          </div>

                          <p
                              className={`max-w-full text-gray-600 [overflow-wrap:anywhere] break-words line-clamp-3 mb-2.5 ${
                              compact ? 'text-xs' : 'text-xs md:text-sm'
                            }`}
                          >
                            {announcement.content}
                          </p>

                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge
                              variant="outline"
                              className={`text-[10px] md:text-xs px-2 py-0 ${config.badgeClass}`}
                            >
                              {config.label}
                            </Badge>
                            {(() => {
                              const audConf = audienceConfig[announcement.audience] || audienceConfig.public;
                              const AudIcon = audConf.icon;
                              return (
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] md:text-xs px-2 py-0 ${audConf.badgeClass}`}
                                >
                                  <AudIcon className="w-3 h-3 mr-1" />
                                  {audConf.label}
                                </Badge>
                              );
                            })()}
                            <span className="text-[10px] md:text-xs text-gray-400 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(announcement.datePosted)}
                            </span>
                            {!compact && (
                              <span className="text-[10px] md:text-xs text-gray-400 hidden sm:inline">
                                by {announcement.postedBy}
                              </span>
                            )}
                          </div>
                        </div>

                        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </ScrollArea>
      )}

      {/* ═══ Holidays Section ═══ */}
      {activeSection === 'holidays' && (
        <div className="space-y-3">
          {sortedHolidays.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CalendarOff className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">No holidays scheduled</p>
              <p className="text-gray-400 text-sm mt-1">Holiday schedules will appear here</p>
            </div>
          ) : (
            <>
              {/* Upcoming holidays highlight */}
              {sortedHolidays.filter((h) => isUpcoming(h.date)).length > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5" />
                    Upcoming Holidays
                  </p>
                </div>
              )}
              {sortedHolidays.map((holiday) => {
                const upcoming = isUpcoming(holiday.date);
                const daysUntil = getDaysUntil(holiday.date);
                const typeConf = holidayTypeConfig[holiday.type] || holidayTypeConfig.regular;

                return (
                  <Card
                    key={holiday.id}
                    className={`group transition-all duration-300 border ${
                      upcoming
                        ? 'border-emerald-200 bg-gradient-to-r from-emerald-50/60 to-white hover:shadow-lg hover:-translate-y-0.5 hover:border-emerald-300'
                        : 'border-gray-200 bg-gray-50/40 opacity-70'
                    }`}
                  >
                    <CardContent className="p-4 md:p-5">
                      <div className="flex items-start gap-3 md:gap-4">
                        {/* Date badge */}
                        <div className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-xl flex flex-col items-center justify-center border transition-transform group-hover:scale-105 ${
                          upcoming
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                            : 'bg-gray-100 border-gray-200 text-gray-400'
                        }`}>
                          <span className="text-[10px] font-bold uppercase leading-none">
                            {new Date(holiday.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                          </span>
                          <span className="text-xl md:text-2xl font-bold leading-none mt-0.5">
                            {new Date(holiday.date + 'T00:00:00').getDate()}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm md:text-base mb-1 truncate">
                            {holiday.name}
                          </h3>
                          <p className="text-xs md:text-sm text-gray-500 mb-2">
                            {formatHolidayDate(holiday.date)}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={`text-[10px] md:text-xs px-2 py-0 ${typeConf.bgColor} ${typeConf.color}`}>
                              {typeConf.label}
                            </Badge>
                            {holiday.recurring && (
                              <Badge variant="outline" className="text-[10px] md:text-xs px-2 py-0 bg-gray-50 text-gray-500 border-gray-200">
                                <Repeat className="w-3 h-3 mr-1" />
                                Annual
                              </Badge>
                            )}
                            {upcoming && daysUntil >= 0 && (
                              <span className={`text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-full ${
                                daysUntil === 0
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : daysUntil <= 7
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-gray-100 text-gray-600'
                              }`}>
                                {daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days away`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ═══ Class Mode Section ═══ */}
      {activeSection === 'classmode' && (
        <div className="space-y-4">
          {classModeSettings.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Monitor className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">No class mode settings</p>
              <p className="text-gray-400 text-sm mt-1">Class mode updates will appear here</p>
            </div>
          ) : (
            <>
              {/* Active class mode highlight card */}
              {activeClassMode && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Monitor className="w-3.5 h-3.5" />
                    Current Mode
                  </p>
                  {(() => {
                    const modeConf = classModeConfig[activeClassMode.mode] || classModeConfig['face-to-face'];
                    const ModeIcon = modeConf.icon;
                    return (
                      <Card className={`border-2 border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50/50 shadow-md overflow-hidden`}>
                        <CardContent className="p-5 md:p-6">
                          <div className="flex items-start gap-4">
                            <div className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br ${modeConf.gradient} flex items-center justify-center shadow-lg`}>
                              <ModeIcon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5">
                                <h3 className="font-bold text-gray-900 text-base md:text-lg">
                                  {modeConf.label}
                                </h3>
                                <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] px-2 py-0">
                                  Active
                                </Badge>
                              </div>
                              {activeClassMode.description && (
                                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                  {activeClassMode.description}
                                </p>
                              )}
                              <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  From {new Date(activeClassMode.effectiveFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                                {activeClassMode.effectiveUntil && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Until {new Date(activeClassMode.effectiveUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                )}
                                {!activeClassMode.effectiveUntil && (
                                  <span className="text-green-600 font-medium">Ongoing</span>
                                )}
                                {activeClassMode.semester && (
                                  <Badge variant="outline" className="text-[10px] px-2 py-0">
                                    Semester {activeClassMode.semester}
                                  </Badge>
                                )}
                                {activeClassMode.schoolYear && (
                                  <Badge variant="outline" className="text-[10px] px-2 py-0">
                                    SY {activeClassMode.schoolYear}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </div>
              )}

              {/* All class mode settings */}
              {classModeSettings.filter((s) => s.id !== activeClassMode?.id).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {activeClassMode ? 'Other Settings' : 'Class Mode Settings'}
                  </p>
                  <div className="space-y-3">
                    {classModeSettings
                      .filter((s) => s.id !== activeClassMode?.id)
                      .sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime())
                      .map((setting) => {
                        const modeConf = classModeConfig[setting.mode] || classModeConfig['face-to-face'];
                        const ModeIcon = modeConf.icon;
                        const isPast = setting.effectiveUntil && new Date(setting.effectiveUntil) < now;

                        return (
                          <Card
                            key={setting.id}
                            className={`group transition-all duration-300 border ${
                              isPast
                                ? 'border-gray-200 opacity-60'
                                : `${modeConf.bgColor} hover:shadow-md hover:-translate-y-0.5`
                            }`}
                          >
                            <CardContent className="p-4 md:p-5">
                              <div className="flex items-start gap-3 md:gap-4">
                                <div className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br ${modeConf.gradient} flex items-center justify-center transition-transform group-hover:scale-105`}>
                                  <ModeIcon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-semibold text-gray-900 text-sm md:text-base">
                                      {modeConf.label}
                                    </h3>
                                    {isPast && (
                                      <Badge variant="outline" className="text-[10px] px-2 py-0 text-gray-400">
                                        Past
                                      </Badge>
                                    )}
                                  </div>
                                  {setting.description && (
                                    <p className="text-xs md:text-sm text-gray-500 mb-2 line-clamp-2">
                                      {setting.description}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2 flex-wrap text-[10px] md:text-xs text-gray-400">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {new Date(setting.effectiveFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      {setting.effectiveUntil && (
                                        <> — {new Date(setting.effectiveUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                                      )}
                                      {!setting.effectiveUntil && ' — Ongoing'}
                                    </span>
                                    {setting.semester && (
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                        Sem {setting.semester}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══ Announcement Detail Modal ═══ */}
      {selectedAnnouncement && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={() => setSelectedAnnouncement(null)}
        >
          <div
            className="bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl w-full sm:max-w-lg max-h-[92vh] sm:max-h-[80vh] overflow-hidden animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const config =
                categoryConfig[selectedAnnouncement.category] || categoryConfig.general;
              const CategoryIcon = config.icon;
              return (
                <>
                  {/* Drag handle for mobile */}
                  <div className="sm:hidden flex justify-center pt-2 pb-0">
                    <div className="w-10 h-1 rounded-full bg-gray-300" />
                  </div>
                  <div className={`${config.bgColor} border-b px-4 sm:px-6 py-4 shrink-0`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-white/60 shadow-sm">
                          <CategoryIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${config.color}`} />
                        </div>
                        <div className="min-w-0 max-w-full flex-1 overflow-hidden">
                          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                            <Badge variant="outline" className={`text-xs ${config.badgeClass}`}>
                              {config.label}
                            </Badge>
                            {(() => {
                              const audConf = audienceConfig[selectedAnnouncement.audience] || audienceConfig.public;
                              const AudIcon = audConf.icon;
                              return (
                                <Badge variant="outline" className={`text-xs ${audConf.badgeClass}`}>
                                  <AudIcon className="w-3 h-3 mr-1" />
                                  {audConf.label}
                                </Badge>
                              );
                            })()}
                            {selectedAnnouncement.isPinned && (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-200">
                                <Pin className="w-2.5 h-2.5 mr-1" />
                                Pinned
                              </Badge>
                            )}
                          </div>
                          <h2 className="max-w-full text-base sm:text-lg font-bold text-gray-900 leading-tight [overflow-wrap:anywhere] break-words overflow-hidden">
                            {selectedAnnouncement.title}
                          </h2>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedAnnouncement(null)}
                        className="p-2 rounded-xl hover:bg-white/60 transition-colors flex-shrink-0 -mt-0.5"
                        aria-label="Close"
                      >
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                    <div className="px-4 sm:px-6 py-4 sm:py-5 pr-8 sm:pr-10 pb-6">
                      <p className="max-w-full text-gray-700 text-sm sm:text-[15px] leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere] break-words overflow-hidden">
                        {selectedAnnouncement.content}
                      </p>
                    </div>
                  </div>

                  <Separator />
                  <div className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-xs text-gray-500 shrink-0">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>
                        Posted{' '}
                        {new Date(selectedAnnouncement.datePosted).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <span className="text-gray-400">by {selectedAnnouncement.postedBy}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
